import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Skeleton } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";

import api from "../../../BrandOnBoarding/api";
import AnimatedNumber from "../../../Component/AnimatedNumber/AnimatedNumber";
import BrandIndexBreakups from "../../../Component/BrandIndexHistory/BrandIndexBreakups";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import Header from "../../../Component/Header/Header";
import Modal from "../../../Component/Modal/Modal";
import Alert from "../../../Component/UI/Alert/Alert";
import { AuthContext } from "../../../context/auth-context";
import { extractDomain } from "../../../utils/helpers";
import Attributes from "../Attributes/Attributes";
import BrandInsights from "../BrandInsights/BrandInsights";
import { DiscoveryContext } from "../discovery-context";
import ProductVariant from "../ProductVariant";
import Share from "../Share/Share";
import GaugeChart from "./../../../Component/GaugeChart/GaugeChart";
import MUICard from "./../../../Component/UI/MUICard/MUICard";
import MUIIconButton from "./../../../Component/UI/MUIIconButton/MUIIconButton";
import LLMIndicator from "./../LLMIndicator/LLMIndicator";
import classes from "./BrandScore.module.css";

export default function BrandScore({ category, market, remountChild }) {
  const [showBrandIndexBreakups, setShowBrandIndexBreakups] = useState(false);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const { user } = useContext(AuthContext);
  const { license_status } = user?.customer;

  const discoveryCtx = useContext(DiscoveryContext);
  const entity = discoveryCtx.entity;

  const discoveryType = discoveryCtx.type || "brand";

  const abortControllerRef = useRef(null);

  async function getBrandIndex() {
    let sentimentCount = 5;
    const isWebSearch =
      discoveryCtx.llm === "gpt-4o-web-search" ||
      discoveryCtx.llm === "gpt-4o-agent-web-search" ||
      discoveryCtx.llm === "GeminiV25Search" || 
      discoveryCtx.llm === "Claude3Search" ||
      discoveryCtx.llm === "gpt-5-search-enabled"

    if (
      discoveryCtx.type === "category" ||
      discoveryCtx.llm === "deepseek_together"
    ) {
      sentimentCount = 1;
    } else if (isWebSearch) {
      sentimentCount = license_status === "Paid" ? 5 : 3;
        if(discoveryCtx.llm === "GeminiV25Search" || discoveryCtx.llm === "Claude3Search" || discoveryCtx.llm === "gpt-5-search-enabled" ){
          sentimentCount = 1;
      }
    }
    const payload = {
      llm: discoveryCtx.llm,
      category: category,
      count: 5,
      brand: discoveryCtx.type === "brand" ? "companies" : "products",
      persona: "General Profile",
      market: market,
      product: entity,
      sentiment_exec_count: sentimentCount,
      return_meta: true,
      language_id: discoveryCtx.language == "it" ? 2 : 1,
      customer_id: user.customer_id,
    };

    setError(null);
    abortControllerRef.current = new AbortController();

    const configs = {
      signal: abortControllerRef.current.signal,
    };

    try {
      const response = await api.post(
        "/metrics/v4/get-brand-index-score?score_type_flag=sentiment",
        payload,
        configs
      );
      const sources = [
        ...Object.keys(response?.data?.sources?.sentiment || {}),
      ];
      discoveryCtx.updateSources(sources);
      const entity = response.data.data[0];
      discoveryCtx.updateBrandScores({
        data: response.data.error
          ? { error: "something went wrong" }
          : {
              name: entity.name,
              favorability: entity.score,
              extras: response.data.extras,
            },
        index: 0,
      });
    } catch (error) {
      discoveryCtx.updateBrandScores({
        data: { error: "something went wrong" },
        index: 0,
      });
      setError(error.message);
    }
  }

  useEffect(() => {
    if (discoveryCtx.abort_launch) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [discoveryCtx.abort_launch]);

  useEffect(() => {
    if (discoveryCtx.brand_scores.length > 0) return;
    getBrandIndex();
  }, [entity, discoveryCtx.brand_scores.length]);

  let brandScore = discoveryCtx.brand_scores[0];
  let loading = true;
  let domain = "";
  let variants = [];

  if (brandScore && brandScore.extras && brandScore.total) {
    brandScore = discoveryCtx.brand_scores[0];
    domain = extractDomain(discoveryCtx.competitors[0]?.domain);
    variants =
      discoveryCtx.competitors[0]?.variants?.filter((v) => v.active) || [];
    loading = false;
  }
  if (brandScore && brandScore.error) {
    loading = false;
  }

  let brandScoreAdjustments = { changed: false };
  if (brandScore?.extras?.rank) {
    const [{ name }] = brandScore?.extras?.rank;
    if (
      name.toString().toLowerCase().trim().replaceAll(" ", "") !==
      discoveryCtx.entity.toString().toLowerCase().trim().replaceAll(" ", "")
    ) {
      brandScoreAdjustments.entities = [name, discoveryCtx.entity];
      brandScoreAdjustments.changed = true;
    }
  }

  return (
    <div className={classes.wrapper}>
      <MUICard variant="elevated" elevation={0} className={classes.card}>
        <div className={classes.container}>
          <div className={classes.brand_container}>
            <Header
              title={
                <div className={classes.title_container}>
                  {domain && (
                    <img
                      className={classes.logo}
                      src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
                    />
                  )}
                  <div className="pt-2">
                    <div className={classes.title}>
                      <span>{entity}</span>
                      {variants.length > 0 && (
                        <ProductVariant
                          variants={variants}
                          triggerId="basic-menu"
                          editable={false}
                        >
                          <span className={classes.variant_text}>
                            {variants.length} variants
                          </span>
                        </ProductVariant>
                      )}
                    </div>
                    {discoveryCtx?.category && (
                      <div className={classes.label_container}>
                        <span className={classes.title_label}>
                          {discoveryCtx?.category}
                        </span>
                        {discoveryCtx?.market ? (
                          <>
                            <span>&middot;</span>
                            <span className={classes.title_label}>
                              {discoveryCtx?.market}
                            </span>
                          </>
                        ) : (
                          <span>&nbsp;</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              }
              titleClassName={classes.title}
              className={classes.header}
              renderAction={() => {
                return (
                  <div className="mx-3">
                    {!discoveryCtx.loading && (
                      <MUIIconButton
                        title="Click to Share"
                        className="mx-3"
                        onClick={() => setShowShareModal(true)}
                      >
                        <ShareOutlinedIcon color="primary" />
                      </MUIIconButton>
                    )}
                  </div>
                );
              }}
            />
            <div className={`${classes.print_only} print-only`}>
              <LLMIndicator llm={discoveryCtx.llm || "gpt-4o"} />
            </div>
            <BrandInsights entity={entity} category={category} />
            <div className={classes.brand_index_wrapper}>
              <div className={classes.brand_index}>
                <div className={classes.main}>
                  {!loading && brandScore?.extras && !error && (
                    <MUIIconButton
                      data-html2canvas-ignore
                      className={classes.info}
                      onClick={() => setShowBrandIndexBreakups(true)}
                    >
                      <InfoOutlinedIcon />
                    </MUIIconButton>
                  )}
                  <div>
                    <span className={classes.brand_label}>
                      <span className={classes.llm_brand}>
                        {discoveryType === "brand" ? "Brand" : "Product"} Index
                        Score
                      </span>
                      <LLMIndicator llm={discoveryCtx.llm || "gpt-4o"} />
                    </span>
                    {loading && !error && <Skeleton width={400} height={350} />}
                    {!loading && brandScore?.total && !error && (
                      <GaugeChart
                        width={"100%"}
                        height={"330px"}
                        radius={"100%"}
                        center={["50%", "70%"]}
                        data={[{ value: brandScore?.total }]}
                      />
                    )}
                    {error && (
                      <div className={classes.error_view}>
                        <ErrorView
                          title={"Error fetching brand index score"}
                          message={error}
                          retry
                          onRetry={() => {
                            discoveryCtx.updateBrandScores({
                              data: null,
                              index: 0,
                            });
                            getBrandIndex();
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className={classes.sub}>
                    <div className={classes.score}>
                      <div className={classes.score_content}>
                        {!error && (
                          <span className={classes.score_text}>Visibility</span>
                        )}
                        {!loading && !error && (
                          <AnimatedNumber
                            className={classes.score_value}
                            start={-1}
                            end={brandScore?.rank || 0}
                            duration={1500}
                          />
                        )}
                        {loading && !error && (
                          <Skeleton width={90} height={110} />
                        )}
                      </div>
                    </div>
                    {!error && <div className={classes.partition}></div>}
                    <div className={classes.score}>
                      <div className={classes.score_content}>
                        {!error && (
                          <span className={classes.score_text}>Sentiment</span>
                        )}
                        {!loading && !error && (
                          <AnimatedNumber
                            className={classes.score_value}
                            start={0}
                            end={brandScore?.favorability || 0}
                            duration={1500}
                          />
                        )}
                        {loading && !error && (
                          <Skeleton width={90} height={110} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {brandScoreAdjustments.changed && (
                  <Alert
                    title="Visibility Score Adjustment"
                    message={
                      <span>
                        The Visibility score has been calculated using the{" "}
                        {discoveryCtx.type}{" "}
                        <b>{brandScoreAdjustments.entities[0]}</b> rather than{" "}
                        <b>{brandScoreAdjustments.entities[1]}</b>
                      </span>
                    }
                  />
                )}
              </div>
              <div className={classes.summary}>
                <Attributes
                  entity={entity}
                  category={category}
                  market={market}
                />
              </div>
            </div>
          </div>
        </div>
        <Modal
          open={showBrandIndexBreakups}
          handleClose={() => setShowBrandIndexBreakups(false)}
          width={1200}
        >
          <BrandIndexBreakups
            data={brandScore?.extras}
            isOpen={showBrandIndexBreakups}
            type={discoveryCtx.type}
            setShowBrandIndexBreakups={setShowBrandIndexBreakups}
            remountChild={remountChild}
          />
        </Modal>
        <Modal
          height="60%"
          title="Share Link"
          open={showShareModal}
          handleClose={() => setShowShareModal(false)}
        >
          <Share setShowShareModal={setShowShareModal} />
        </Modal>
      </MUICard>
    </div>
  );
}
